import { BufferAttribute, BufferGeometry, LineBasicMaterial, LineSegments, type Vector3 } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugLineConfig } from '../types';

export interface AddLineParams {
	config: DebugLineConfig;
}

/**
 * Add a debug line
 */
export const addLineCfn = (
	self: DebugManager,
	{ config }: AddLineParams
): LineSegments | null => {
	const { id, start, end, color = 0xffff00 } = config;

	// Validate start and end positions - skip if any coordinate is NaN
	if (!Number.isFinite(start.x) || !Number.isFinite(start.y) || !Number.isFinite(start.z)) {
		console.warn(`DebugManager.addLine: Skipping line '${id}' with invalid start: [${start.x}, ${start.y}, ${start.z}]`);
		return null;
	}
	if (!Number.isFinite(end.x) || !Number.isFinite(end.y) || !Number.isFinite(end.z)) {
		console.warn(`DebugManager.addLine: Skipping line '${id}' with invalid end: [${end.x}, ${end.y}, ${end.z}]`);
		return null;
	}

	// Remove existing line with same ID
	if (self.hasLine(id)) {
		self.removeLine(id);
	}

	// Create line
	const geometry = new BufferGeometry();
	const positions = new Float32Array([
		start.x, start.y, start.z,
		end.x, end.y, end.z
	]);
	geometry.setAttribute('position', new BufferAttribute(positions, 3));

	const material = new LineBasicMaterial({ color, depthTest: false });
	const line = new LineSegments(geometry, material);
	line.name = `debug_line_${id}`;
	line.visible = self.visible;
	line.renderOrder = 999; // Render on top
	// Mark as interface object to exclude from wireframe mode
	line.userData.isInterface = true;
	line.userData.type = 'debugVisualization';

	self.scene.add(line);
	self.registerLine(id, line);

	return line;
};

export interface UpdateLineParams {
	id: string;
	start: Vector3;
	end: Vector3;
}

/**
 * Update a debug line
 */
export const updateLineCfn = (
	self: DebugManager,
	{ id, start, end }: UpdateLineParams
): void => {
	const line = self.getLine(id);
	if (line) {
		const positions = line.geometry.getAttribute('position') as BufferAttribute;
		positions.setXYZ(0, start.x, start.y, start.z);
		positions.setXYZ(1, end.x, end.y, end.z);
		positions.needsUpdate = true;
	}
};

export interface RemoveLineParams {
	id: string;
}

/**
 * Remove a debug line
 */
export const removeLineCfn = (
	self: DebugManager,
	{ id }: RemoveLineParams
): void => {
	const line = self.getLine(id);
	if (line) {
		self.scene.remove(line);
		line.geometry.dispose();
		(line.material as LineBasicMaterial).dispose();
		self.unregisterLine(id);
	}
};
